import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useTalentCalendar } from '../../contexts/TalentCalendarContext';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import Select from 'react-select';
import TalentWeekView from './TalentWeekView';
import './Calendar.css';

const TalentCalendar = ({ onEventClick, onDateSelect, initialFilters = {}, events: propsEvents }) => {
  // Use context with fallback values
  const contextValues = useTalentCalendar() || {};
  
  const {
    currentDate = new Date(),
    selectedDate = new Date(),
    events: contextEvents = [],
    view = 'week',
    setView = () => {},
    goToPreviousMonth = () => {},
    goToNextMonth = () => {},
    goToPreviousWeek = () => {},
    goToNextWeek = () => {},
    goToPreviousDay = () => {},
    goToNextDay = () => {},
    goToToday = () => {},
    loading = false,
    setEventFilters = () => {},
    userOptions = [],
    clientOptions = [], 
    setOptions = [], 
    locationOptions = [], 
    statusOptions = [],
    getDaysForWeekView = () => [],
    setSelectedDate = () => {}
  } = contextValues;

  // Use events from props if provided, otherwise use context events
  const events = propsEvents || contextEvents;

  // Selected filters
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSet, setSelectedSet] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Apply initial filters on mount
  useEffect(() => {
    if (Object.keys(initialFilters).length > 0) {
      if (initialFilters.bookingStatus) {
        const option = statusOptions.find(opt => opt.value === initialFilters.bookingStatus);
        setSelectedStatus(option || null);
      }
      
      if (initialFilters.userEmail) {
        const option = userOptions.find(opt => opt.value === initialFilters.userEmail);
        setSelectedUser(option || null);
      }
      
      if (initialFilters.client) {
        const option = clientOptions.find(opt => opt.value === initialFilters.client);
        setSelectedClient(option || null);
      }
      
      if (initialFilters.set) {
        const option = setOptions.find(opt => opt.value === initialFilters.set);
        setSelectedSet(option || null);
      }
      
      if (initialFilters.location) {
        const option = locationOptions.find(opt => opt.value === initialFilters.location);
        setSelectedLocation(option || null);
      }
      
      setEventFilters(initialFilters);
    }
  }, [initialFilters, userOptions, clientOptions, setOptions, locationOptions, statusOptions, setEventFilters]);
  
  // Handle filter changes
  const handleFilterChange = (type, option) => {
    const value = option?.value;
    
    switch(type) {
      case 'status':
        setSelectedStatus(option);
        setEventFilters({ bookingStatus: value });
        break;
      case 'user':
        setSelectedUser(option);
        setEventFilters({ userEmail: value });
        break;
      case 'client':
        setSelectedClient(option);
        setEventFilters({ client: value });
        break;
      case 'set':
        setSelectedSet(option);
        setEventFilters({ set: value });
        break;
      case 'location':
        setSelectedLocation(option);
        setEventFilters({ location: value });
        break;
      default:
        break;
    }
  };

  // Handle search changes
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setEventFilters({ searchTerm: term });
  };

  // Clear all filters including search
  const clearAllFilters = () => {
    setSelectedStatus(null);
    setSelectedUser(null);
    setSelectedClient(null);
    setSelectedSet(null);
    setSelectedLocation(null);
    setSearchTerm('');
    setEventFilters({});
  };

  // Handle navigation based on current view
  const handlePrevious = () => {
    switch (view) {
      case 'month':
        goToPreviousMonth();
        break;
      case 'week':
        goToPreviousWeek();
        break;
      case 'day':
        goToPreviousDay();
        break;
      default:
        break;
    }
  };

  const handleNext = () => {
    switch (view) {
      case 'month':
        goToNextMonth();
        break;
      case 'week':
        goToNextWeek();
        break;
      case 'day':
        goToNextDay();
        break;
      default:
        break;
    }
  };

  // Handle view change
  const handleViewChange = (newView) => {
    setView(newView);
  };

  // Handle event click
  const handleEventClick = (event) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };
  
  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (onDateSelect) {
      onDateSelect(date);
    }
  };

  // Render the appropriate view
  const renderView = () => {
    switch (view) {
      case 'month':
        return <MonthView onEventClick={handleEventClick} onDateSelect={handleDateSelect} />;
      case 'week':
        return <TalentWeekView onEventClick={handleEventClick} onDateSelect={handleDateSelect} events={events} />;
      case 'day':
        return <DayView onEventClick={handleEventClick} onDateSelect={handleDateSelect} />;
      default:
        return <TalentWeekView onEventClick={handleEventClick} onDateSelect={handleDateSelect} events={events} />;
    }
  };

  const renderWeekView = () => {
    return (
      <TalentWeekView
        currentDate={currentDate}
        selectedDate={selectedDate}
        events={events}
        onDateSelect={handleDateSelect}
        onEventClick={handleEventClick}
        getDaysForWeekView={getDaysForWeekView}
      />
    );
  };

  // Custom styles for Select components
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minWidth: '150px',
      margin: '0 5px',
      fontSize: '14px'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };

  return (
    <div className="talent-calendar-container">
      <div className="calendar-header">
        <h2>{currentDate.toLocaleDateString('default', { month: 'long', year: 'numeric' })}</h2>
      </div>
      
      {/* Search functionality */}
      <div className="talent-calendar-search" 
           style={{ 
             display: 'flex', 
             alignItems: 'center', 
             marginBottom: '20px',
             gap: '10px',
             padding: '0 10px'
           }}>
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search bookings by talent, client, location..."
          className="talent-search-input"
          style={{
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            minWidth: '300px',
            flex: 1
          }}
        />
        {searchTerm && (
          <button
            onClick={clearAllFilters}
            className="clear-search-btn"
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              background: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear
          </button>
        )}
      </div>
      
      <TalentWeekView
        onEventClick={handleEventClick}
        onDateSelect={handleDateSelect}
        events={events}
      />
    </div>
  );
};

export default TalentCalendar; 